// src/features/executive/export/format.ts
// Pure formatters for Phase 1F export (PDF + Excel). No React, no i18n — callers inject labels.

import type { ExecutiveFilterState } from 'api/executive/executive.types'

/** `2026-04-20-1145` — local timezone, no seconds, filename-safe. */
export function formatTimestamp(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  const y = d.getFullYear()
  const m = pad(d.getMonth() + 1)
  const day = pad(d.getDate())
  const hh = pad(d.getHours())
  const mm = pad(d.getMinutes())
  return `${y}-${m}-${day}-${hh}${mm}`
}

/** Human-readable timestamp used in print footer + Excel metadata. */
export function formatReadableTimestamp(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  const y = d.getFullYear()
  const m = pad(d.getMonth() + 1)
  const day = pad(d.getDate())
  const hh = pad(d.getHours())
  const mm = pad(d.getMinutes())
  return `${y}-${m}-${day} ${hh}:${mm}`
}

/** Strip filesystem-unsafe chars and collapse whitespace to `-`. Keeps Thai/EN letters & digits. */
function sanitizeForFilename(s: string): string {
  return s
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '')
    .replace(/\s+/g, '-')
}

/** `<clinic>-executive-performance-2026-04-20-1145` (clinic prefix omitted if blank) */
export function buildPdfFilename(tab: string, clinicName?: string, d: Date = new Date()): string {
  const prefix = clinicName ? `${sanitizeForFilename(clinicName)}-` : ''
  return `${prefix}executive-${tab}-${formatTimestamp(d)}`
}

/** `<clinic>-executive-revenue-2026-04-20-1145.xlsx` (clinic prefix omitted if blank) */
export function buildExcelFilename(drillId: string, clinicName?: string, d: Date = new Date()): string {
  const prefix = clinicName ? `${sanitizeForFilename(clinicName)}-` : ''
  return `${prefix}executive-${drillId}-${formatTimestamp(d)}.xlsx`
}

/**
 * Compose a short filter summary like `"สาขา=ทองหล่อ · สัญชาติ=ญี่ปุ่น"`
 * for display in Excel metadata / print filter bar.
 *
 * Caller passes a lookup fn for each scalar key → human label so i18n /
 * name resolution (e.g. branchId → branchName) stays out of this module.
 */
export interface FilterSummaryInput {
  filters: ExecutiveFilterState
  dateRangeLabel: string
  branchLabel: string
  scalarLabels: Partial<Record<keyof ExecutiveFilterState, { label: string; value: string }>>
}

export function composeFilterSummary(input: FilterSummaryInput): string {
  const parts: string[] = []
  parts.push(`ช่วงเวลา=${input.dateRangeLabel}`)
  parts.push(`สาขา=${input.branchLabel}`)
  Object.entries(input.scalarLabels).forEach(([_, v]) => {
    if (v) parts.push(`${v.label}=${v.value}`)
  })
  return parts.join(' · ')
}
